import * as angular from 'angular';
import { gpHome } from '../app';
import { CurrentUserService, ISubscriptionInfo } from '../../app-common/services/current-user-service';
import { IAuthService } from './auth-service';
import { isAiV2FeatureFlagEnabled } from '../../app-common/util/feature-flags';
import { AiCreditsServiceName, AiCreditBalance, IAiCreditsApi } from './ai-credits-contract';
import {
    AiCreditsPurchaseServiceName,
    AiCreditPurchaseOptions,
    IAiCreditsPurchaseApi,
} from './ai-credits-purchase-contract';
import { AccountDataKey, AccountDataStore, AccountPrefetchServiceName } from './account-data-cache';

import type * as ng from 'angular';

/**
 * Fetches what the account page needs, and remembers it (#1229).
 *
 * Every `peek` is synchronous and every `fetch` stores what it gets, so a panel
 * opening for the second time draws the previous figures at once and lets the
 * refresh replace them in place. The panels still own their own presentation and
 * their own "Refresh all" wiring — this only removes the wait.
 */
export interface IAccountPrefetchApi {
    /**
     * Start filling the cache. Called once as app-home starts, so the round trips
     * overlap the home page rather than the account page.
     */
    prefetch(): void;

    peekSubscriptionInfo(): ISubscriptionInfo | null;
    peekCreditBalance(): AiCreditBalance | null;
    peekCreditPurchaseOptions(): AiCreditPurchaseOptions | null;

    /**
     * @param checkPayment also run `api/subscription/check` first, as the account
     * page does on open. Left off when prefetching: that call reaches Stripe, and
     * app-home loads far more often than the account page is opened.
     */
    fetchSubscriptionInfo(checkPayment: boolean): ng.IPromise<ISubscriptionInfo>;
    fetchCreditBalance(): ng.IPromise<AiCreditBalance>;
    fetchCreditPurchaseOptions(): ng.IPromise<AiCreditPurchaseOptions>;
}

class AccountPrefetchService implements IAccountPrefetchApi {

    static serviceName = AccountPrefetchServiceName;
    static $inject = [
        'authService',
        CurrentUserService.serviceName,
        AiCreditsServiceName,
        AiCreditsPurchaseServiceName,
    ];

    private store = new AccountDataStore();

    /**
     * One entry per key while a request is outstanding. The account page opening on
     * top of the startup prefetch is the ordinary case, so callers share the request
     * already running instead of adding a second one.
     */
    private inFlight = new Map<AccountDataKey, ng.IPromise<unknown>>();

    /** Also records whether the outstanding request includes the payment check. */
    private subscriptionInfoInFlight: {
        promise: ng.IPromise<ISubscriptionInfo>;
        withPaymentCheck: boolean;
    } | null = null;

    constructor(
        private readonly authService: IAuthService,
        private readonly currentUserService: CurrentUserService,
        private readonly aiCreditsService: IAiCreditsApi,
        private readonly aiCreditsPurchaseService: IAiCreditsPurchaseApi,
    ) {
        this.authService.authorisationChanged(status => {
            if (!status.isAuthorised) {
                this.store.clear();
                return;
            }
            this.store.setOwner(status.userId || status.email);
        });
    }

    prefetch(): void {
        // Nothing on the sign-out path has an account to fetch, and the token is
        // still present for the moment, so the requests would otherwise succeed and
        // leave one person's figures cached for the next.
        if (window.location.pathname === '/logout') {
            return;
        }

        this.authService.checkAuthorisationIfNeeded()
            .then(status => {
                if (!status.isAuthorised) {
                    return;
                }

                this.fetchSubscriptionInfo(false);

                if (isAiV2FeatureFlagEnabled()) {
                    this.fetchCreditBalance();
                    this.fetchCreditPurchaseOptions();
                }
            })
            .catch(() => { /* Not signed in, or the check failed — nothing to prefetch. */ });
    }

    peekSubscriptionInfo() {
        return this.copyOf(this.store.peek<ISubscriptionInfo>('subscriptionInfo'));
    }

    peekCreditBalance() {
        return this.copyOf(this.store.peek<AiCreditBalance>('creditBalance'));
    }

    peekCreditPurchaseOptions() {
        return this.copyOf(this.store.peek<AiCreditPurchaseOptions>('creditPurchaseOptions'));
    }

    fetchSubscriptionInfo(checkPayment: boolean) {
        // Sharing has a direction here. A caller that wanted no payment check is
        // happy with a checked request's answer; a caller that asked for one must
        // NOT be handed the startup prefetch's, or opening the account page straight
        // from a link would silently stop checking for a payment settled since.
        let entry = this.subscriptionInfoInFlight;
        if (entry && !entry.withPaymentCheck && checkPayment) {
            entry = null;
        }

        if (!entry) {
            const request = checkPayment
                ? this.currentUserService.checkSubscriptionPayment()
                    .then(() => this.currentUserService.getSubscriptionInfo())
                : this.currentUserService.getSubscriptionInfo();

            const started = {
                promise: request.then(info => {
                    this.store.put('subscriptionInfo', info, Date.now());
                    return info;
                }),
                withPaymentCheck: checkPayment,
            };

            // Guarded, so a checked request that overtook an unchecked one does not
            // clear the entry the later request replaced it with. See `load` for why
            // both arms rather than `finally`.
            const forget = () => {
                if (this.subscriptionInfoInFlight === started) {
                    this.subscriptionInfoInFlight = null;
                }
            };
            started.promise.then(forget, forget);

            this.subscriptionInfoInFlight = started;
            entry = started;
        }

        // Copied per caller: the panel edits the payload it is handed (defaulting
        // `subscriptions`, collapsing `licensesManagedByOthers`), so the stored object
        // must not be the one it edits.
        return entry.promise.then(info => angular.copy(info));
    }

    fetchCreditBalance() {
        return this.load<AiCreditBalance>('creditBalance', () => this.aiCreditsService.getBalance());
    }

    fetchCreditPurchaseOptions() {
        return this.load<AiCreditPurchaseOptions>(
            'creditPurchaseOptions', () => this.aiCreditsPurchaseService.getOptions());
    }

    private load<T>(key: AccountDataKey, request: () => ng.IPromise<T>): ng.IPromise<T> {
        let pending = this.inFlight.get(key) as ng.IPromise<T> | undefined;

        if (!pending) {
            pending = request().then(value => {
                this.store.put(key, value, Date.now());
                return value;
            });

            // Both arms, rather than `finally`: a rejection with no handler on this
            // branch would surface as $q's "possibly unhandled rejection". A failed
            // refresh stores nothing, so whatever the panel is already showing stays
            // its last known figures.
            const forget = () => { this.inFlight.delete(key); };
            pending.then(forget, forget);

            this.inFlight.set(key, pending);
        }

        // Copied per caller, so what the store holds stays the server's answer
        // whatever a reader does with its own.
        return pending.then(value => angular.copy(value));
    }

    private copyOf<T>(value: T | null): T | null {
        return value === null ? null : angular.copy(value);
    }
}

gpHome.service(AccountPrefetchService.serviceName, AccountPrefetchService);
